/**
 * In-app notifications — request schemas + wire types (docs/NOTIFICATIONS.md).
 *
 * Served by `apps/api/src/routes/platform/notifications.ts` over the reshaped
 * `notifications` table of migration `20260921175320_channel_notifications.sql`:
 *
 *   GET  /api/v1/notifications/orgs/:orgId?limit=N&status=unread|all
 *       → `{ items: NotificationWire[], unread_count }` (`NotificationsListResponse`)
 *         The org filter is `organization_id = :orgId OR organization_id IS NULL`
 *         — NULL-org rows are system / cross-org and show in every org's bell.
 *         `status=unread` (the bell's default view) narrows the list to
 *         `read_at IS NULL`; `all` (the schema default) is the newest rows
 *         regardless. `unread_count` is the same head count either way.
 *   POST /api/v1/notifications/orgs/:orgId/read
 *       → `{ updated }` (`MarkNotificationsReadResponse`)
 *         Body is the strict union `{ ids }` | `{ all: true }`.
 *
 * The browser never inserts a notification (no INSERT policy for any client
 * role) — emitters are DEFINER functions calling `notify_users()`. RLS pins
 * `user_id` to the caller on both routes, so neither takes a user parameter.
 *
 * The wire interfaces are shared by the API handlers (response construction)
 * and the web hooks (unwrap<T> targets) so the two sides cannot drift.
 */
import { z } from "zod";
// ---------------------------------------------------------------------------
// Request schemas
// ---------------------------------------------------------------------------
/** The bell's two views: the unread queue, or the newest rows read or not. */
export const NOTIFICATION_LIST_STATUSES = ["unread", "all"];
/**
 * `?limit=` + `?status=` for the list read — limit 1..100, default 30 (the bell
 * is a menu, not a page); status defaults to `all` so an older web keeps its
 * pre-`status` behaviour.
 */
export const notificationsListQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(30),
    status: z.enum(NOTIFICATION_LIST_STATUSES).default("all"),
});
/**
 * POST …/read body — EITHER a bounded id list OR the literal `{ all: true }`.
 * Both arms are `.strict()` so `{ ids: [...], all: true }` is a 400, not a
 * silent "mark everything" — the union's second arm must never be reachable
 * by accident.
 */
export const markNotificationsReadSchema = z.union([
    z.object({ ids: z.array(z.string().uuid()).min(1).max(500) }).strict(),
    z.object({ all: z.literal(true) }).strict(),
]);
//# sourceMappingURL=notifications.js.map